import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';
import { resolveCountryFlagIconUrl } from '../../utils/country-flag.util';

export interface CountriesSelectOption {
  value: string;
  label: string;
  iso?: string;
  iconUrl?: string;
}

/**
 * Catálogo de países para selects y listados.
 * GET {apiUrl}/selects/countries → { success, data: [{ id, code, text, iconUrl? }] }
 */
@Injectable({ providedIn: 'root' })
export class CountriesSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, CountriesSelectOption[]>();
  private readonly endpointUrl = '/selects/countries';

  getOptions(): Observable<CountriesSelectOption[]> {
    const cached = this.cache.get(this.endpointUrl);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(this.endpointUrl, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): CountriesSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id: string;
          code?: string;
          text?: string;
          label?: string;
          name?: string;
          iconUrl?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => {
        const iso = item.code?.trim().toUpperCase() || undefined;
        return {
          value: item.id || '',
          label: item.text || item.label || item.name || '',
          iso,
          iconUrl: resolveCountryFlagIconUrl({
            iso,
            iconUrl: item.iconUrl,
          }),
        };
      });
    } catch {
      return [];
    }
  }
}
